import { HTTPError } from 'ky';
import { z } from 'zod';

import { staticFeatureFlags } from '@/config/features';
import type { ClientCategorisation } from '@/constants';
import { BINARY_ANSWERS, NOT_APPLICABLE_VALUE } from '@/constants/answers';
import {
  BusinessType,
  ControlType,
  EmirClassification,
  EmirThreshold,
  EsmaClassification,
  FinancialOrganisationType,
  NaceClassification,
  ORGANISATION_TYPES_REGULATED_FINANCIAL_SERVICES,
  OrganisationType,
} from '@/constants/institution';
import i18n from '@/lib/i18n';
import { fastApi } from '@/services/api';
import { getCountryPostalCodeUse, mainstreamCountrySchema } from '@/services/api/mainstream';
import { NationalClientIdentifierType } from '@/utils/national-client-identifier';

const PATH = 'v1/customer/form/institution-details';

export const institutionDetailsIncorporationSchema = z.object({
  countryOfIncorporation: mainstreamCountrySchema(i18n.t('institutionDetails.incorporation.errors.countryOfIncorporation')),
});

export const institutionDetailsBranchOperationsDifferentJurisdictionSchema = z.object({
  differentJurisdiction: z.enum(BINARY_ANSWERS),
});

export const institutionDetailsBranchOperationsJurisdictionSchema = z.object({
  countryOfBranchOperations: mainstreamCountrySchema(i18n.t('institutionDetails.branchOperations.countryOfBranchOperations.errors.countryOfBranchOperations')),
});

export const institutionDetailsBranchOperationsSchema = z.discriminatedUnion(
  'differentJurisdiction',
  [
    z.object({
      differentJurisdiction: z.literal('NO'),
      countryOfBranchOperations: z.undefined(),
    }),
    z.object({
      differentJurisdiction: z.literal('YES'),
      countryOfBranchOperations: institutionDetailsBranchOperationsJurisdictionSchema.shape.countryOfBranchOperations,
    }),
  ],
  {
    error: i18n.t('institutionDetails.branchOperations.errors.differentJurisdiction'),
  },
);

export const institutionDetailsRegisteredAddressSchema = z
  .object({
    addressLines: z.array(z.string().min(1)).min(1).max(2),
    country: z.string({ error: i18n.t('institutionDetails.company.errors.registeredAddressCountry') }).min(1, i18n.t('institutionDetails.company.errors.registeredAddressCountry')),
    locality: z.string({ error: i18n.t('institutionDetails.company.errors.registeredAddressCity') }).min(1, i18n.t('institutionDetails.company.errors.registeredAddressCity')),
    postalCode: z.string({ error: i18n.t('institutionDetails.company.errors.registeredAddressPostCode') }),
  })
  .superRefine(async (address, context) => {
    if (address.postalCode.trim() !== '') return;

    if ((await getCountryPostalCodeUse(address.country)) === 'required') {
      context.addIssue({ code: 'custom', message: i18n.t('institutionDetails.company.errors.registeredAddressPostCode'), path: ['postalCode'] });
    }
  });

export type InstitutionDetailsRegisteredAddress = z.infer<typeof institutionDetailsRegisteredAddressSchema>;

export const institutionDetailsCompanySchema = z.object({
  companyName: z.string({ error: i18n.t('institutionDetails.company.errors.companyName') }).min(1, i18n.t('institutionDetails.company.errors.companyName')),
  registeredAddress: z.union([institutionDetailsRegisteredAddressSchema, z.string().min(1, i18n.t('institutionDetails.company.errors.registeredAddress'))]),
  typeOfBusiness: z.enum(Object.values(BusinessType), {
    error: i18n.t('institutionDetails.company.errors.typeOfBusiness'),
  }),
  taxId: z
    .string({ error: i18n.t('institutionDetails.company.errors.taxId') })
    .min(1, i18n.t('institutionDetails.company.errors.taxId'))
    .regex(/^[a-zA-Z0-9]+$/, i18n.t('institutionDetails.company.errors.taxIdInvalid')),
  leiNumber: z
    .string()
    .regex(/^[a-zA-Z0-9]{20}$/, i18n.t('institutionDetails.company.errors.leiNumberInvalid'))
    .optional(),
});

export type InstitutionDetailsCompany = z.infer<typeof institutionDetailsCompanySchema>;

export const institutionDetailsRegulatoryInformationSchema = z.object({
  hasRegulatorySupervisionOrOversight: z.enum(BINARY_ANSWERS, {
    error: i18n.t('institutionDetails.regulatoryInformation.errors.hasRegulatorySupervisionOrOversight'),
  }),
  licenseOrAuthorisationRegistrationDetails: z.string().min(1, i18n.t('institutionDetails.regulatoryInformation.errors.licenseOrAuthorisationRegistrationDetails')),
});

export const institutionDetailsNationalClientIdentifierSchema = z.discriminatedUnion(
  'nationalClientIdentifierType',
  [
    z.object({
      nationalClientIdentifierType: z.literal(NationalClientIdentifierType.CONCAT),
      nationalClientIdentifier: z.string().optional(),
    }),
    z.object({
      nationalClientIdentifierType: z.enum([NationalClientIdentifierType.CCPT, NationalClientIdentifierType.NIDN]),
      nationalClientIdentifier: z
        .string({ error: i18n.t('institutionDetails.individuals.individual.errors.nationalClientIdentifier') })
        .min(1, i18n.t('institutionDetails.individuals.individual.errors.nationalClientIdentifier')),
    }),
  ],
  {
    error: i18n.t('institutionDetails.individuals.individual.errors.nationalClientIdentifierType'),
  },
);

export const institutionDetailsIndividualBaseSchema = z.object({
  individualId: z.uuidv4(),
  firstName: z.string().min(1, i18n.t('institutionDetails.individuals.individual.errors.firstName')),
  lastName: z.string().min(1, i18n.t('institutionDetails.individuals.individual.errors.lastName')),
  dateOfBirth: z.iso.date(i18n.t('institutionDetails.individuals.individual.errors.dateOfBirth')),
  countryOfBirth: mainstreamCountrySchema(i18n.t('institutionDetails.individuals.individual.errors.countryOfBirth')),
  localityOfBirth: z.string().min(1, i18n.t('institutionDetails.individuals.individual.errors.localityOfBirth')),
  nationality: mainstreamCountrySchema(i18n.t('institutionDetails.individuals.individual.errors.nationality')),
});

export const institutionDetailsIndividualSchema = institutionDetailsIndividualBaseSchema.and(institutionDetailsNationalClientIdentifierSchema);

export const institutionDetailsAlgorithmSchema = z.object({
  algorithmId: z
    .string({ error: i18n.t('institutionDetails.algorithm.errors.algorithmId') })
    .regex(/^[A-Z0-9]+$/, i18n.t('institutionDetails.algorithm.errors.algorithmIdInvalid'))
    .min(1, i18n.t('institutionDetails.algorithm.errors.algorithmId')),
});

const getInstitutionDetailsDecisionMakerIndividualsError = () =>
  staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED
    ? i18n.t('institutionDetails.decisionMaker.errors.individualsExactlyOne')
    : i18n.t('institutionDetails.decisionMaker.errors.individuals');

export const institutionDetailsDecisionMakerSchema = z.discriminatedUnion(
  'decisionMaker',
  [
    z.object({
      decisionMaker: z.literal(ControlType.INDIVIDUAL),
      individuals: z
        .array(institutionDetailsIndividualSchema, { error: getInstitutionDetailsDecisionMakerIndividualsError })
        .min(1, { error: getInstitutionDetailsDecisionMakerIndividualsError })
        .max(staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED ? 1 : Number.POSITIVE_INFINITY, { error: getInstitutionDetailsDecisionMakerIndividualsError }),
    }),
    z.object({
      decisionMaker: z.literal(ControlType.ALGORITHM),
      algorithm: institutionDetailsAlgorithmSchema,
    }),
  ],
  {
    error: i18n.t('institutionDetails.decisionMaker.errors.decisionMaker'),
  },
);

export type InstitutionDetailsDecisionMaker = z.infer<typeof institutionDetailsDecisionMakerSchema>;

const institutionDetailsExecutionIndividualsError = () =>
  staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED
    ? i18n.t('institutionDetails.execution.errors.individualsExactlyOne')
    : i18n.t('institutionDetails.execution.errors.individuals');

export const institutionDetailsExecutionSchema = z.discriminatedUnion(
  'execution',
  [
    z.object({
      execution: z.literal(ControlType.INDIVIDUAL),
      individuals: z
        .array(institutionDetailsIndividualSchema, { error: institutionDetailsExecutionIndividualsError })
        .min(1, { error: institutionDetailsExecutionIndividualsError })
        .max(staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED ? 1 : Number.POSITIVE_INFINITY, { error: institutionDetailsExecutionIndividualsError }),
    }),
    z.object({
      execution: z.literal(ControlType.ALGORITHM),
      algorithm: institutionDetailsAlgorithmSchema,
    }),
  ],
  {
    error: i18n.t('institutionDetails.execution.errors.execution'),
  },
);

export type InstitutionDetailsExecution = z.infer<typeof institutionDetailsExecutionSchema>;

const institutionDetailsClientCategorisationOrganisationTypeRegulatedFinancialServicesSchema = z.object({
  organisationType: z.enum(ORGANISATION_TYPES_REGULATED_FINANCIAL_SERVICES),
  financialOrganisationType: z.enum(Object.values(FinancialOrganisationType), {
    error: i18n.t('institutionDetails.clientCategorisation.errors.financialOrganisationType'),
  }),
});

const institutionDetailsClientCategorisationOrganisationTypeOtherBaseSchema = z.object({
  organisationType: z.enum([
    OrganisationType.COLLECTIVE_INVESTMENT_SCHEME,
    OrganisationType.PENSION_FUND,
    OrganisationType.COMMODITY_DEALER,
    OrganisationType.OTHER_INSTITUTIONAL_INVESTOR,
    OrganisationType.GOVERNMENT,
    OrganisationType.CENTRAL_BANK,
    OrganisationType.NONE_OF_THE_ABOVE,
  ]),
});

export const institutionDetailsClientCategorisationGeneralSchema = z.discriminatedUnion(
  'organisationType',
  [institutionDetailsClientCategorisationOrganisationTypeRegulatedFinancialServicesSchema, institutionDetailsClientCategorisationOrganisationTypeOtherBaseSchema],
  {
    error: i18n.t('institutionDetails.clientCategorisation.errors.organisationType'),
  },
);

const institutionDetailsClientCategorisationAdditionalPrimarySchema = z.object({
  hasBalanceSheetAssetsAboveAmount: z.enum(BINARY_ANSWERS, {
    error: i18n.t('institutionDetails.clientCategorisation.errors.hasBalanceSheetAssetsAboveAmount'),
  }),
  hasNetTurnoverAboveAmount: z.enum(BINARY_ANSWERS, {
    error: i18n.t('institutionDetails.clientCategorisation.errors.hasNetTurnoverAboveAmount'),
  }),
  hasOwnFundsAboveAmount: z.enum(BINARY_ANSWERS, {
    error: i18n.t('institutionDetails.clientCategorisation.errors.hasOwnFundsAboveAmount'),
  }),
});

export const CLIENT_CATEGORISATION_SECONDARY_QUESTIONS_NO_COUNT_THRESHOLD = 2;

const institutionDetailsClientCategorisationAdditionalSchemaRefinement = (data: z.infer<typeof institutionDetailsClientCategorisationAdditionalSchema>, ctx: z.RefinementCtx) => {
  const primaryQuestionNoCount = [data.hasBalanceSheetAssetsAboveAmount, data.hasNetTurnoverAboveAmount, data.hasOwnFundsAboveAmount].filter((value) => value === 'NO').length;
  if (primaryQuestionNoCount < CLIENT_CATEGORISATION_SECONDARY_QUESTIONS_NO_COUNT_THRESHOLD) {
    return;
  }

  if (!data.hasPortfolioOverAmount) {
    ctx.addIssue({
      code: 'custom',
      message: i18n.t('institutionDetails.clientCategorisation.errors.hasPortfolioOverAmount'),
      path: ['hasPortfolioOverAmount'],
    });
  }

  if (!data.hasRepresentativeTransactions) {
    ctx.addIssue({
      code: 'custom',
      message: i18n.t('institutionDetails.clientCategorisation.errors.hasRepresentativeTransactions'),
      path: ['hasRepresentativeTransactions'],
    });
  }

  if (!data.hasRepresentativeWorkedInFinancialSector) {
    ctx.addIssue({
      code: 'custom',
      message: i18n.t('institutionDetails.clientCategorisation.errors.hasRepresentativeWorkedInFinancialSector'),
      path: ['hasRepresentativeWorkedInFinancialSector'],
    });
  }

  if (data.hasRepresentativeWorkedInFinancialSector === 'YES' && !data.hasRepresentativeKnowledgeOfTransactions) {
    ctx.addIssue({
      code: 'custom',
      message: i18n.t('institutionDetails.clientCategorisation.errors.hasRepresentativeKnowledgeOfTransactions'),
      path: ['hasRepresentativeKnowledgeOfTransactions'],
    });
  }
};

export const institutionDetailsClientCategorisationAdditionalSchema = institutionDetailsClientCategorisationAdditionalPrimarySchema
  .extend({
    hasRepresentativeWorkedInFinancialSector: z
      .enum(BINARY_ANSWERS, {
        error: i18n.t('institutionDetails.clientCategorisation.errors.hasRepresentativeWorkedInFinancialSector'),
      })
      .optional(),
    hasRepresentativeKnowledgeOfTransactions: z
      .enum(BINARY_ANSWERS, {
        error: i18n.t('institutionDetails.clientCategorisation.errors.hasRepresentativeKnowledgeOfTransactions'),
      })
      .optional(),
    hasPortfolioOverAmount: z
      .enum(BINARY_ANSWERS, {
        error: i18n.t('institutionDetails.clientCategorisation.errors.hasPortfolioOverAmount'),
      })
      .optional(),
    hasRepresentativeTransactions: z
      .enum(BINARY_ANSWERS, {
        error: i18n.t('institutionDetails.clientCategorisation.errors.hasRepresentativeTransactions'),
      })
      .optional(),
  })
  .superRefine(institutionDetailsClientCategorisationAdditionalSchemaRefinement);

const institutionDetailsClientCategorisationOrganisationTypeOtherSchema = institutionDetailsClientCategorisationOrganisationTypeOtherBaseSchema
  .extend(institutionDetailsClientCategorisationAdditionalSchema.shape)
  .superRefine(institutionDetailsClientCategorisationAdditionalSchemaRefinement);

export const institutionDetailsClientCategorisationSchema = z.discriminatedUnion(
  'organisationType',
  [institutionDetailsClientCategorisationOrganisationTypeRegulatedFinancialServicesSchema, institutionDetailsClientCategorisationOrganisationTypeOtherSchema],
  {
    error: i18n.t('institutionDetails.clientCategorisation.errors.organisationType'),
  },
);

export const institutionDetailsReportingEmirYesBaseSchema = z.object({
  emir: z.literal('YES'),
});

export const EMIR_RISK_REDUCTION_CONFIRMATION_OPTIONS = [...BINARY_ANSWERS, NOT_APPLICABLE_VALUE] as const;

export const institutionDetailsReportingEmirReportingResponsibilityNoSchema = institutionDetailsReportingEmirYesBaseSchema.extend({
  emirUtiAcknowledgment: z.literal(true, {
    error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirUtiAcknowledgment'),
  }),
  emirPostTradeRiskConfirmation: z.literal(true, {
    error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirPostTradeRiskConfirmation'),
  }),
});

const institutionDetailsReportingEmirSchema = z
  .discriminatedUnion(
    'emir',
    [
      z.discriminatedUnion(
        'emirReportingResponsibility',
        [
          institutionDetailsReportingEmirYesBaseSchema.extend({
            emirReportingResponsibility: z.literal('YES'),
          }),
          institutionDetailsReportingEmirReportingResponsibilityNoSchema.extend({
            emirReportingResponsibility: z.literal('NO'),
          }),
        ],
        {
          error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirReportingResponsibility'),
        },
      ),
      z.object({
        emir: z.literal('NO'),
      }),
    ],
    {
      error: i18n.t('institutionDetails.reporting.errors.emir'),
    },
  )
  .and(
    z.discriminatedUnion(
      'emirClassification',
      [
        z.object({
          emirClassification: z.literal(EmirClassification.FINANCIAL_COUNTERPARTY),
          esmaClassification: z.enum(Object.values(EsmaClassification), {
            error: i18n.t('institutionDetails.reporting.emirDetails.errors.esmaClassification'),
          }),
        }),
        z.object({
          emirClassification: z.literal(EmirClassification.NON_FINANCIAL_COUNTERPARTY),
          naceClassification: z.enum(Object.values(NaceClassification), {
            error: i18n.t('institutionDetails.reporting.emirDetails.errors.naceClassification'),
          }),
          emirRiskReductionConfirmation: z.enum(EMIR_RISK_REDUCTION_CONFIRMATION_OPTIONS, {
            error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirRiskReductionConfirmation'),
          }),
        }),
        z.object({
          emirClassification: z.literal(EmirClassification.OTHER),
        }),
      ],
      {
        error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirClassification'),
      },
    ),
  )
  .and(
    z.object({
      emirThreshold: z.enum(Object.values(EmirThreshold), {
        error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirThreshold'),
      }),
    }),
  );

export const institutionDetailsReportingSchema = z
  .object({
    mifid: z.enum(BINARY_ANSWERS, {
      error: i18n.t('institutionDetails.reporting.errors.mifid'),
    }),
  })
  .and(institutionDetailsReportingEmirSchema);

export const institutionDetailsAuthorisationSchema = z.object({
  confirmation: z.literal(true, {
    error: i18n.t('institutionDetails.authorisation.errors.confirmation'),
  }),
  fullName: z.string().min(1, i18n.t('institutionDetails.authorisation.errors.fullName')),
  date: z.iso.datetime(i18n.t('institutionDetails.authorisation.errors.date')),
});

export const institutionDetailsOnboardingSchema = z.object({
  incorporation: institutionDetailsIncorporationSchema,
  branchOperations: institutionDetailsBranchOperationsSchema,
  company: institutionDetailsCompanySchema,
  regulatoryInformation: institutionDetailsRegulatoryInformationSchema,
  authorisation: institutionDetailsAuthorisationSchema,
});

export type InstitutionDetailsOnboarding = z.infer<typeof institutionDetailsOnboardingSchema>;

export type InstitutionDetailsOnboardingKey = keyof InstitutionDetailsOnboarding;

const institutionDetailsOnboardingUpdateSchema = z.union([
  z.strictObject({
    company: z.object({
      leiNumber: institutionDetailsCompanySchema.shape.leiNumber.nonoptional(),
    }),
  }),
]);

type InstitutionDetailsOnboardingUpdate = z.infer<typeof institutionDetailsOnboardingUpdateSchema>;

export const institutionDetailsFuturesSchema = z.object({
  decisionMaker: institutionDetailsDecisionMakerSchema,
  execution: institutionDetailsExecutionSchema,
  clientCategorisation: institutionDetailsClientCategorisationSchema,
  reporting: institutionDetailsReportingSchema,
});

export type InstitutionDetailsFutures = z.infer<typeof institutionDetailsFuturesSchema>;

export type InstitutionDetailsFuturesKey = keyof InstitutionDetailsFutures;

export const institutionDetailsSchema = institutionDetailsOnboardingSchema.extend(institutionDetailsFuturesSchema.shape);

export type InstitutionDetails = z.infer<typeof institutionDetailsSchema>;

export type InstitutionDetailsKey = keyof InstitutionDetails;

export const institutionDetailsPartialSchema = institutionDetailsSchema.partial();

export type InstitutionDetailsPartial = z.infer<typeof institutionDetailsPartialSchema>;

export type InstitutionDetailsGetResponse =
  | {
      answers: InstitutionDetails;
      categorisation: ClientCategorisation;
      regulated: boolean;
    }
  | {
      answers: InstitutionDetailsPartial;
    };

type InstitutionDetailsPostResponse = {
  answers: InstitutionDetailsPartial;
};

export const getInstitutionDetails = async () => {
  return fastApi
    .get(PATH)
    .json<InstitutionDetailsGetResponse>()
    .catch((error) => {
      if (error instanceof HTTPError && error.response.status === 404) return null;

      throw error;
    });
};

export const postInstitutionDetails = async (body: InstitutionDetailsPartial | InstitutionDetailsOnboardingUpdate) => {
  return fastApi
    .post(PATH, {
      json: {
        answers: body,
      },
    })
    .json<InstitutionDetailsPostResponse>()
    .then(({ answers }) => answers);
};
